import {useApiQuery} from 'src/hooks/useApiQuery';
import type {
  AnswerMap,
  AnswerValue,
  AssetListItem,
  AssetListResponse,
  ConversationListResponse,
  QuestionnaireQuestion,
} from 'src/types/ask-ai';


export const POSITION_PERSIST_KEY = 'askai:chatPos';
export const PANEL_LEFT_MARGIN = 0;
export const SIDENAV_EXPANDED_WIDTH = 240;
export const SIDENAV_COLLAPSED_WIDTH = 60;
export const FULLSCREEN_MODE_KEY = 'fscr';
export const ASSET_ID_QUERY_KEY = 'assetId';
export const DEFAULT_ASK_AI_ELEVATION = 2000;
export const DEFAULT_CONVERSATION_NAME = 'Untitled conversation';

// Exact sentinel the grace backend matches to abandon a sub chat window (doc §5.3).
// Must be sent verbatim: lowercase, no trailing punctuation.
export const SUBCHAT_ABANDON_SENTINEL = 'abandon the sub chat window';

export const getMessageHistoryQueryKey = (threadId: string) => [
  'message-history',
  threadId,
];

export const CONVERSATION_LIST_QUERY_KEY = ['conversation-list'] as const;
export const ASSET_LIST_QUERY_KEY = ['asset-list'] as const;
export const getAssetQueryKey = (assetId: string) => ['asset', assetId];

export function useConversationListQuery({
  enabled = true,
}: {enabled?: boolean} = {}) {
  return useApiQuery<ConversationListResponse>('conversation', {
    queryOptions: {queryKey: CONVERSATION_LIST_QUERY_KEY, enabled},
    apiOptions: {
      method: 'GET',
      options: {
        apiPath: '/grace/api/v1/',
      },
    },
  });
}

export function useAssetListQuery({enabled = true}: {enabled?: boolean} = {}) {
  return useApiQuery<AssetListResponse>('assets', {
    queryOptions: {queryKey: ASSET_LIST_QUERY_KEY, enabled},
    apiOptions: {
      method: 'GET',
      options: {
        apiPath: '/grace/api/v1/',
      },
    },
  });
}

// Fetch a single asset by id. The response shape matches an individual item
// from the asset list (`AssetListItem`).
export function useAssetQuery(
  assetId: string,
  {enabled = true}: {enabled?: boolean} = {},
) {
  return useApiQuery<AssetListItem>(`assets/${assetId}`, {
    queryOptions: {
      queryKey: getAssetQueryKey(assetId),
      enabled: enabled && Boolean(assetId),
    },
    apiOptions: {
      method: 'GET',
      options: {
        apiPath: '/grace/api/v1/',
      },
    },
  });
}

export function formatConversationLastModifiedAt(raw?: string) {
  if (!raw) {
    return '';
  }

  const date = new Date(raw);

  if (Number.isNaN(date.getTime())) {
    return raw;
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatSelect(
  question: QuestionnaireQuestion,
  answer: AnswerValue,
): string {
  const pieces = answer.selectedIds.map((id) => {
    const opt = question.options?.find((o) => o.id === id);
    if (opt?.is_freeform) {
      // Freeform: "<id> | <freeform text>" per §5.5.
      return `${opt.id} | ${(answer.freeformText ?? '').trim()}`;
    }
    return `${opt?.id ?? id} | ${opt?.label ?? id}`;
  });
  return pieces.join(', ');
}

/**
 * Build the `Q: <title>\nA: <answer>` text string the backend expects (doc §5.5).
 * Skipped questions (and `skip` question_type) are omitted entirely.
 *
 * The result is rendered as markdown (remark-gfm), where a single "\n" is a soft
 * break that collapses to a space. So we join Q→A with a hard break (two trailing
 * spaces + "\n") to keep them on separate lines, and separate each Q/A block with
 * a blank line ("\n\n") for a visible gap.
 */
export function formatAnswers(
  questions: QuestionnaireQuestion[],
  answers: AnswerMap,
): string {
  const lines: string[] = [];

  for (const q of questions) {
    if (q.question_type === 'skip') {
      continue;
    }
    const a = answers[q.id];
    if (!a || a.skipped) {
      continue;
    }

    let answerText: string;
    if (q.question_type === 'text') {
      answerText = (a.text ?? '').trim();
      if (answerText === '') {
        continue;
      }
    } else {
      if (a.selectedIds.length === 0) {
        continue;
      }
      answerText = formatSelect(q, a);
    }

    lines.push(`Q: ${q.title}  \nA: ${answerText}`);
  }

  return lines.join('\n\n');
}
